import { Component, EventEmitter, Input, Output } from '@angular/core';

export type DashboardLayoutPreset = 'balanced' | 'sidebar' | 'full';

// cols: the button's icon, column rectangles as [x, width] in a 24 x 24 box (mockup .layout-preset).
export const DASHBOARD_LAYOUT_PRESETS: { value: DashboardLayoutPreset, label: string, title: string, cols: number[][] }[] = [
    { value: 'balanced', label: '50/50', title: '50/50 equal columns', cols: [[3, 7], [14, 7]] },
    { value: 'sidebar', label: '66/33', title: '66/33 with right sidebar', cols: [[3, 11], [18, 3]] },
    { value: 'full', label: '100%', title: '100% full width', cols: [[3, 18]] }
];

/** A saved preset if it is still one of the presets, otherwise the fallback. */
export function dashboardLayoutPreset(saved: any, fallback: DashboardLayoutPreset): DashboardLayoutPreset {
    return DASHBOARD_LAYOUT_PRESETS.some(p => p.value === saved) ? saved : fallback;
}

// <app-dashboard-layout-presets [preset]="preset" (presetChange)="setPreset($event)"> — the
// mockup's .dashboard-controls Layout buttons, shared by the administrator and inspector
// dashboards (each saves its own preference and sets the columns).
@Component({
    selector: 'app-dashboard-layout-presets',
    template: `
        <div class="dl-layout">
            <span class="dl-label">Layout</span>
            <div class="dl-presets" role="group" aria-label="Dashboard layout">
                <button *ngFor="let p of presets" type="button" class="dl-preset" [attr.aria-pressed]="preset === p.value"
                    [attr.aria-label]="p.title" [title]="p.title" (click)="presetChange.emit(p.value)">
                    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
                        <rect *ngFor="let c of p.cols" [attr.x]="c[0]" y="4" [attr.width]="c[1]" height="16" rx="1"></rect>
                    </svg>
                    <span>{{p.label}}</span>
                </button>
            </div>
        </div>`,
    styles: [`
        :host { display: flex; flex-wrap: wrap; align-items: center; justify-content: flex-end; gap: 12px; }
        .dl-layout { display: flex; align-items: center; gap: 10px; margin-left: auto; color: var(--im-navy); font-size: 14px; font-weight: 800; }
        .dl-presets { display: inline-flex; gap: 4px; padding: 4px; border: 1px solid var(--im-line); border-radius: 9px; background: var(--im-soft); }
        .dl-preset { min-height: 40px; display: inline-flex; align-items: center; gap: 6px; padding: 0 10px; border: 1px solid transparent; border-radius: 6px; color: var(--im-muted);
            background: transparent; font: inherit; font-size: 14px; font-weight: 800; cursor: pointer; }
        .dl-preset svg { width: 16px; height: 16px; flex: 0 0 16px; }
        .dl-preset:hover { color: var(--im-navy); background: var(--im-surface); }
        .dl-preset[aria-pressed="true"] { border-color: #9ebde0; color: #174f91; background: #e4effc; box-shadow: 0 1px 3px rgba(18, 45, 82, .08); }
        .dl-preset:focus-visible { outline: 3px solid #ffcf66; outline-offset: 2px; }
        :host-context(body.dark-mode) .dl-preset[aria-pressed="true"] { border-color: #7091b9; color: #e3efff; background: #254568; }
        :host-context(body.colorblind-mode) .dl-preset[aria-pressed="true"] { border-color: #79b6d0; color: #005b84; background: #e4f4fb; }
        @media (max-width: 780px) {
            .dl-layout { width: 100%; margin-left: 0; justify-content: space-between; }
        }
    `]
})
export class DashboardLayoutPresetsComponent {
    @Input() preset: DashboardLayoutPreset;
    @Output() presetChange = new EventEmitter<DashboardLayoutPreset>();
    readonly presets = DASHBOARD_LAYOUT_PRESETS;
}
